import Editor from '@ckeditor/ckeditor5-core/src/editor/editor';
import attachToForm from '@ckeditor/ckeditor5-core/src/editor/utils/attachtoform';
import DataApiMixin from '@ckeditor/ckeditor5-core/src/editor/utils/dataapimixin';
import ElementApiMixin from '@ckeditor/ckeditor5-core/src/editor/utils/elementapimixin';
import HtmlDataProcessor from '@ckeditor/ckeditor5-engine/src/dataprocessor/htmldataprocessor';
import mix from '@ckeditor/ckeditor5-utils/src/mix';
import { DocumentId, ProjectId } from 'api/models';
import { logger } from 'services';
import { CustomEditorUI } from './custom-editor-ui';

// Extending the Editor class, which brings the base editor API.
export class CustomEditor extends Editor {
  [x: string]: any;

  constructor(element: Element, config) {
    super(config);

    // Remember the element the editor is created with.
    this.sourceElement = element;
    // Use the HTML data processor in this editor.
    this.data.processor = new HtmlDataProcessor(this.data.viewDocument);

    // Create the ("main") root element of the model tree.
    this.model.document.createRoot();

    // The UI layer of the editor.
    this.ui = new CustomEditorUI(this);

    // When editor#element is a textarea inside a form element,
    // the content of this textarea will be updated on form submit.
    attachToForm(this);
  }

  destroy() {
    // When destroyed, the editor sets the output of editor#getData() into editor#element...
    this.updateSourceElement();

    // ...and destroys the UI.
    this.ui.destroy();

    return super.destroy();
  }

  static create(
    element,
    config,
    info?: {
      projectId: ProjectId;
      docId: DocumentId;
      accountId?: number;
      onInitialisationError?: () => void;
    },
  ): Editor {
    const { onInitialisationError } = info || {};

    return new Promise((resolve) => {
      const editor = new this(element, config);

      resolve(
        editor
          .initPlugins()
          // Initialize the UI first. See the CustomEditorUI class to learn more.
          .then(() => editor.ui.init(element))
          // Fill the editable with the initial data.
          .then(() => {
            const editorPromise = new Promise((reslv) => {
              // throw new Error();

              return reslv(editor.data.init(config.initialData));
            });

            editorPromise.catch((error) => {
              logger.error('error in custom editor', { error });

              if (onInitialisationError) {
                onInitialisationError();
              }
            });

            return editorPromise;
          })
          // Fire the `editor#ready` event that announce the editor is complete and ready to use.
          .then(() => editor.fire('ready'))
          .then(() => editor),
      );
    });
  }
}

// Mixing interfaces, which extends the basic editor API.
mix(CustomEditor, DataApiMixin);
mix(CustomEditor, ElementApiMixin);
